/* global L */

// Load the unminified version of leaflet.vectorgrid
import "leaflet.vectorgrid/dist/Leaflet.VectorGrid.bundled.js";
import { isNumber } from "lodash";

const originalUpdateIcon = L.SVG.Tile.prototype._updateIcon;
const originalUpdateStyle = L.SVG.Tile.prototype._updateStyle;

// Flag hidden features on their SVG element to let the stylesheet hide them. Their
// opacity can't be used for this, as the vector grid forces `pointer-events: auto` on its
// SVG elements, which keeps transparent features hoverable and clickable.
//
L.SVG.Tile.prototype._updateStyle = function (layer) {
  originalUpdateStyle.call(this, layer);

  layer._path?.toggleAttribute("data-hidden", !!layer.options.isHidden);
};

// Override the `_updateIcon` function of the leaflet vector grid SVG renderer to allow
// showing a pointer cursor when the marker should be clickable and hiding the marker
// when needed.
//
L.SVG.Tile.prototype._updateIcon = layer => {
  const previousPath = layer._path;

  // Call the origin function that will create the new `<path>` SVG element and set the
  // basic style attributes.
  originalUpdateIcon(layer);

  const path = layer._path;
  const { opacity, isClickable, isHidden } = layer.options;

  // Set the opacity to allow hiding the marker when needed
  if (isNumber(opacity)) path.setAttribute("opacity", opacity);
  // Set the cursor to pointer when the marker should be clickable
  path.setAttribute("data-interactive", isClickable);
  // Hide the marker when its element is filtered out
  path.toggleAttribute("data-hidden", !!isHidden);

  // If the previous existing path was already added to the DOM, force the new path to
  // replace it, as Leaflet.VectorGrid doesn't seem to do it automatically.
  if (previousPath?.parentNode) {
    previousPath.parentNode.replaceChild(path, previousPath);
  }
};

L.VectorGrid.prototype.createTile = function (coords, done) {
  var storeFeatures = this.options.getFeatureId;

  var tileSize = this.getTileSize();
  var renderer = this.options.rendererFactory(coords, tileSize, this.options);

  var vectorTilePromise = this._getVectorTilePromise(coords);

  if (storeFeatures) {
    this._vectorTiles[this._tileCoordsToKey(coords)] = renderer;
    renderer._features = {};
  }

  vectorTilePromise.then(
    function renderTile(vectorTile) {
      // Create an array to store the layers with their z-order
      var layersWithZIndex = [];

      // Iterate over the layers to collect their z-orders and layer names
      for (var layerName in vectorTile.layers) {
        var layerStyle = this.options.vectorTileLayerStyles[layerName];
        if (layerStyle instanceof Function) {
          layerStyle = layerStyle(
            vectorTile.layers[layerName].features[0].properties,
            coords.z
          );
        }
        var zIndex = layerStyle.zIndex !== undefined ? layerStyle.zIndex : 0;
        layersWithZIndex.push({ zIndex, layerName });
      }

      // Sort the layers by zIndex
      layersWithZIndex.sort((a, b) => a.zIndex - b.zIndex);

      // Render each layer in the sorted order
      layersWithZIndex.forEach(layerInfo => {
        var layerName = layerInfo.layerName;
        this._dataLayerNames[layerName] = true;
        var layer = vectorTile.layers[layerName];

        var pxPerExtent = this.getTileSize().divideBy(layer.extent);

        var styleOptions =
          this.options.vectorTileLayerStyles[layerName] || L.Path.prototype.options;

        for (var i = 0; i < layer.features.length; i++) {
          var feat = layer.features[i];
          var id;

          if (storeFeatures) {
            id = this.options.getFeatureId(feat);
            var styleOverride = this._overriddenStyles[id];
            if (styleOverride) {
              if (styleOverride[layerName]) {
                styleOptions = styleOverride[layerName];
              } else {
                styleOptions = styleOverride;
              }
            }
          }

          if (styleOptions instanceof Function) {
            styleOptions = styleOptions(feat.properties, coords.z);
          }

          if (!(styleOptions instanceof Array)) {
            styleOptions = [styleOptions];
          }

          if (!styleOptions.length) {
            continue;
          }

          var featureLayer = this._createLayer(feat, pxPerExtent);

          for (var j = 0; j < styleOptions.length; j++) {
            var style = L.extend({}, L.Path.prototype.options, styleOptions[j]);
            featureLayer.render(renderer, style);
            renderer._addPath(featureLayer);
          }

          if (this.options.interactive) {
            featureLayer.makeInteractive();
          }

          if (storeFeatures) {
            renderer._features[id] = {
              layerName: layerName,
              feature: featureLayer
            };
          }
        }
      });

      if (this._map != null) {
        renderer.addTo(this._map);
      }
      L.Util.requestAnimFrame(done.bind(coords, null, null));
    }.bind(this)
  );

  return renderer.getContainer();
};
